import React, { useState, useEffect, useRef } from 'react';
import { X, Mic, MicOff } from 'lucide-react';

const VoiceOrbModal = ({ 
  isVisible,
  onClose,
  onMinimize,
  waveformData,
  voiceStatus,
  selectedVoiceHost,
  micMuted,
  voiceWaveformColor,
  voiceManager,
  messages,
  onSendMessage,
  setMicMuted
}) => {
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [voiceState, setVoiceState] = useState('listening');
  const [dotHeights, setDotHeights] = useState([22, 22, 22, 22]);
  const [localMicMuted, setLocalMicMuted] = useState(micMuted);
  const closeTimerRef = useRef(null);
  const lastUpdateRef = useRef(0);
  const frameCountRef = useRef(0);
  const smoothHeightsRef = useRef([22, 22, 22, 22]);
  const animationFrameRef = useRef(null);

  // Update voice state based on voiceManager's internal state
  useEffect(() => {
    if (!voiceManager) return;

    const checkState = () => {
      const isAISpeaking = voiceManager.isAISpeaking || voiceManager.isPlayingAudio;
      const isUserSpeaking = waveformData?.isActive && waveformData?.height > 15;
      const isThinking = voiceStatus && (
        voiceStatus.toLowerCase().includes('thinking') || 
        voiceStatus.toLowerCase().includes('processing')
      );

      if (isAISpeaking) {
        setVoiceState('speaking');
      } else if (localMicMuted) {
        setVoiceState('muted');
      } else if (isUserSpeaking) {
        setVoiceState('user-speaking');
      } else if (isThinking) {
        setVoiceState('thinking');
      } else {
        setVoiceState('listening');
      }
    };

    checkState();
    const interval = setInterval(checkState, 150);
    return () => clearInterval(interval);
  }, [waveformData, voiceStatus, voiceManager, localMicMuted]);

  useEffect(() => {
    setLocalMicMuted(micMuted);
  }, [micMuted]);

  // ✅ OPTIMIZED HEIGHT UPDATE WITH SMOOTH EASING & FRAME SKIPPING
  useEffect(() => {
    if (!waveformData?.frequencyData) return;

    const animateHeights = () => {
      const now = Date.now();
      
      // ✅ THROTTLE: Only update every 40ms (~25fps for smooth mobile perf)
      if (now - lastUpdateRef.current < 40) {
        animationFrameRef.current = requestAnimationFrame(animateHeights);
        return;
      }
      
      lastUpdateRef.current = now;

      const freqData = waveformData.frequencyData;
      const targetHeights = [
        freqData[8] || 0,
        freqData[18] || 0,
        freqData[28] || 0,
        freqData[38] || 0
      ].map(sample => {
        const normalized = Math.min(sample / 255, 1);
        const minHeight = 20;
        const maxHeight = 65;
        return minHeight + (normalized * (maxHeight - minHeight));
      });

      // ✅ SMOOTH INTERPOLATION: Gradually move towards target (easing)
      const easing = 0.15; // Lower = smoother, higher = more responsive
      const newHeights = smoothHeightsRef.current.map((current, idx) => {
        const target = targetHeights[idx];
        // Linear interpolation for smooth movement
        return current + (target - current) * easing;
      });

      smoothHeightsRef.current = newHeights;

      // ✅ BATCH UPDATES: Only call setState every other frame
      frameCountRef.current++;
      if (frameCountRef.current % 2 === 0) {
        setDotHeights(newHeights.map(h => Math.round(h * 2) / 2)); // Round to 0.5px
      }

      animationFrameRef.current = requestAnimationFrame(animateHeights);
    };

    animationFrameRef.current = requestAnimationFrame(animateHeights);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [waveformData?.frequencyData]);

  const handleClose = () => {
    if (voiceManager) {
      try {
        voiceManager.interruptCurrentAudio?.();
        voiceManager.stopListening?.();
      } catch (e) {
        console.warn('⚠️ Error stopping audio:', e);
      }
    }

    setIsAnimatingOut(true);
    closeTimerRef.current = setTimeout(() => {
      onClose();
      setIsAnimatingOut(false);
    }, 350);
  };

  const handleMinimize = () => {
    if (voiceManager) {
      try {
        voiceManager.interruptCurrentAudio?.();
      } catch (e) {
        console.warn('⚠️ Error stopping audio:', e);
      }
    }

    setIsAnimatingOut(true);
    closeTimerRef.current = setTimeout(() => {
      onMinimize();
      setIsAnimatingOut(false);
    }, 350);
  };

  const handleToggleMute = () => {
    const newMutedState = !localMicMuted;
    console.log('🎤 Orb Modal Toggle mute:', newMutedState);
    
    setLocalMicMuted(newMutedState);
    
    if (setMicMuted) {
      setMicMuted(newMutedState);
    }
    
    if (voiceManager?.toggleMicrophone) {
      voiceManager.toggleMicrophone(newMutedState);
    }
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (voiceManager) {
        try {
          voiceManager.interruptCurrentAudio?.();
        } catch (e) {
          console.warn('⚠️ Error in cleanup:', e);
        }
      }
    };
  }, [voiceManager]);

  const getStatusText = () => {
    if (voiceState === 'speaking') {
      return localMicMuted ? 'AI speaking (mic muted)' : 'Speak to interrupt';
    }
    if (localMicMuted) return "You're muted";
    if (voiceState === 'user-speaking') return 'Listening...';
    if (voiceState === 'thinking') return 'Thinking...';
    return 'Listening...';
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`orb-modal-overlay ${isAnimatingOut ? 'animating-out' : ''}`}
      onClick={handleMinimize}
    >
      <div 
        className={`orb-modal-container ${isAnimatingOut ? 'slide-down' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="orb-modal-title">Voice Mode</div>

        <div className="orb-center-section">
          {/* Listening State - Grey dots with smooth dynamic height */}
          {voiceState === 'listening' && (
            <div className="dots-alex-container show">
              <div className="dot alex dot-1 listening" style={{ height: `${dotHeights[0]}px` }}></div>
              <div className="dot alex dot-2 listening" style={{ height: `${dotHeights[1]}px` }}></div>
              <div className="dot alex dot-3 listening" style={{ height: `${dotHeights[2]}px` }}></div>
              <div className="dot alex dot-4 listening" style={{ height: `${dotHeights[3]}px` }}></div>
            </div>
          )}

          {/* User Speaking - Smooth dynamic height with grey color */}
          {voiceState === 'user-speaking' && (
            <div className="dots-alex-container show">
              <div className="dot alex dot-1 user-speaking" style={{ height: `${dotHeights[0]}px` }}></div>
              <div className="dot alex dot-2 user-speaking" style={{ height: `${dotHeights[1]}px` }}></div>
              <div className="dot alex dot-3 user-speaking" style={{ height: `${dotHeights[2]}px` }}></div>
              <div className="dot alex dot-4 user-speaking" style={{ height: `${dotHeights[3]}px` }}></div>
            </div>
          )}

          {/* AI Speaking - Blue dots with smooth dynamic height */}
          {voiceState === 'speaking' && (
            <div className="dots-alex-container show">
              <div className="dot alex dot-1 speaking" style={{ height: `${dotHeights[0]}px` }}></div>
              <div className="dot alex dot-2 speaking" style={{ height: `${dotHeights[1]}px` }}></div>
              <div className="dot alex dot-3 speaking" style={{ height: `${dotHeights[2]}px` }}></div>
              <div className="dot alex dot-4 speaking" style={{ height: `${dotHeights[3]}px` }}></div>
              {localMicMuted && (
                <div className="muted-indicator">
                  <MicOff size={20} color="#ef4444" strokeWidth={2} />
                </div>
              )}
            </div>
          )}

          {/* Muted State - Static grey dots */}
          {voiceState === 'muted' && (
            <div className="dots-alex-container show">
              <div className="dot alex dot-1 muted-state" style={{ height: '22px' }}></div>
              <div className="dot alex dot-2 muted-state" style={{ height: '22px' }}></div>
              <div className="dot alex dot-3 muted-state" style={{ height: '22px' }}></div>
              <div className="dot alex dot-4 muted-state" style={{ height: '22px' }}></div>
              <div className="muted-icon-center">
                <MicOff size={28} color="#ef4444" strokeWidth={1.5} />
              </div>
            </div>
          )}

          {/* Thinking State - Bouncing dots */}
          {voiceState === 'thinking' && (
            <div className="thinking-indicator">
              <div className="dot thinking dot-1"></div>
              <div className="dot thinking dot-2"></div>
              <div className="dot thinking dot-3"></div>
            </div>
          )}
        </div>

        <div className="orb-status-text">
          <span>{getStatusText()}</span>
        </div>

        <div className="orb-controls-section">
          <button 
            className={`orb-control-btn mute-button ${localMicMuted ? 'is-muted' : ''}`}
            onClick={handleToggleMute}
            title={localMicMuted ? 'Unmute' : 'Mute'}
          >
            <div className="btn-inner">
              {localMicMuted ? (
                <MicOff size={20} color="#ef4444" strokeWidth={1.5} />
              ) : (
                <Mic size={20} color="#1a1a1a" strokeWidth={1.5} />
              )}
            </div>
          </button>

          <button 
            className="orb-control-btn chat-button"
            onClick={handleMinimize}
            title="Back to Chat"
          >
            <div className="btn-inner">Switch to Chat</div>
          </button>

          <button 
            className="orb-control-btn close-button"
            onClick={handleClose}
            title="Close"
          >
            <div className="btn-inner">
              <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="#1a1a1a" viewBox="0 0 24 24">
  <path d="M7 5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7Z"/>
</svg>

            </div>
          </button>
        </div>

        <style jsx>{`
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }

          .orb-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.4);
            display: flex;
            align-items: flex-end;
            justify-content: center;
            z-index: 10000;
            animation: fadeInSmooth 0.25s ease-out;
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
          }

          .orb-modal-overlay.animating-out {
            animation: fadeOutSmooth 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          }

          @keyframes fadeInSmooth {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes fadeOutSmooth {
            from { opacity: 1; }
            to { opacity: 0; }
          }

          .orb-modal-container {
            width: 100%;
            max-width: 500px;
            height: 90vh;
            max-height: 750px;
            background: linear-gradient(170deg, #fefefeff 0%, #fefefeff 60%, #e0e7fcff 100%);
            border-radius: 28px 28px 0 0;
            padding: 24px 20px 40px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            animation: slideUpSmooth 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
            box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
          }

          .orb-modal-container.slide-down {
            animation: slideDownSmooth 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          }

          @keyframes slideUpSmooth {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }

          @keyframes slideDownSmooth {
            from { transform: translateY(0); }
            to { transform: translateY(100%); }
          }

          .orb-modal-title {
            font-size: 16px;
            font-weight: 500;
            color: #333;
            margin-bottom: 16px;
          }

          .orb-center-section {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            margin: 20px 0;
            min-height: 120px;
            position: relative;
          }

          .dots-alex-container {
            display: none;
            align-items: center;
            justify-content: center;
            gap: 8px;
            height: 80px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }

          .dots-alex-container.show {
            display: flex;
          }

          .dot.alex {
            background: linear-gradient(45deg, #4e86da, #78a3c980, #3c8dcb);
            border-radius: 50px;
            width: 22px;
            position: relative;
            overflow: hidden;
            will-change: height;
            transform: translateZ(0);
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
            -webkit-perspective: 1000;
            perspective: 1000;
          }

          /* ✅ SMOOTH HEIGHT TRANSITION - No delay, immediate response */
          .dot.alex {
            transition: height 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
          }

          /* Bluish plasma effect */
          .dot.alex::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: conic-gradient(
              from 0deg,
              #e0f7ff,
              #b6e2f8,
              #94d0f4,
              #66b8e6,
              #3c8dcb,
              #2f75b5,
              #1e5d9f,
              #10498a,
              #0a356f,
              #e0f7ff
            );
            animation: plasmaRotate 3s linear infinite;
            z-index: -1;
          }

          .dot.alex::after {
            content: '';
            position: absolute;
            top: 1px;
            left: 1px;
            right: 1px;
            bottom: 1px;
            background: linear-gradient(145deg, #6fbcfff7, #3c8dcb);
            border-radius: 50px;
            z-index: -1;
          }

          @keyframes plasmaRotate {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* Listening state - Grey with smooth response */
          .dot.alex.listening {
            background: linear-gradient(45deg, #9ca3af, #6b7280, #9ca3af);
          }

          .dot.alex.listening::before {
            background: linear-gradient(
              from 0deg,
              rgba(156, 163, 175, 0.8),
              rgba(107, 114, 128, 0.6),
              rgba(156, 163, 175, 0.8)
            );
          }

          .dot.alex.listening::after {
            background: linear-gradient(145deg, #d1d5dbb6, #9ca3af);
          }

          /* User speaking - Grey with smooth response */
          .dot.alex.user-speaking {
            background: linear-gradient(45deg, #9ca3af, #6b7280, #9ca3af);
          }

          .dot.alex.user-speaking::after {
            background: linear-gradient(145deg, #d1d5dbb6, #9ca3af);
          }

          /* AI speaking state - Blue with smooth response */
          .dot.alex.speaking {
            background: linear-gradient(45deg, #4e86da, #78a3c980, #3c8dcb);
          }

          /* Muted state */
          .dot.alex.muted-state {
            background: linear-gradient(45deg, #9ca3af, #6b7280, #9ca3af);
            opacity: 0.4;
            filter: grayscale(0.6);
          }

          .muted-icon-center {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 10;
            animation: pulse-mute 2s ease-in-out infinite;
          }

          @keyframes pulse-mute {
            0%, 100% { 
              opacity: 1; 
              transform: translate(-50%, -50%) scale(1); 
            }
            50% { 
              opacity: 0.7; 
              transform: translate(-50%, -50%) scale(1.1); 
            }
          }

          .muted-indicator {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 10;
            animation: pulse-mute 2s ease-in-out infinite;
          }

          /* Thinking State */
          .thinking-indicator {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
          }

          .dot.thinking {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #4a90e2;
            animation: thinkingBounce 1.4s ease-in-out infinite;
          }

          .dot-1 { animation-delay: 0s; }
          .dot-2 { animation-delay: 0.2s; }
          .dot-3 { animation-delay: 0.4s; }

          @keyframes thinkingBounce {
            0%, 100% {
              transform: translateY(0);
              opacity: 0.6;
            }
            50% {
              transform: translateY(-16px);
              opacity: 1;
            }
          }

          .orb-status-text {
            font-size: 13px;
            color: #666;
            font-style: italic;
            font-weight: 400;
            margin-bottom: 24px;
            min-height: 18px;
          }

          .orb-controls-section {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding-top: 16px;
            border-top: 1px solid rgba(0, 0, 0, 0.08);
          }

          .orb-control-btn {
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .mute-button {
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: linear-gradient(145deg, #ffffff, #f5f5f5);
            box-shadow: 
              12px 12px 24px rgba(0, 0, 0, 0.15),
              -12px -12px 24px rgba(255, 255, 255, 0.8),
              inset 2px 2px 4px rgba(0, 0, 0, 0.05);
          }

          .mute-button.is-muted {
            background: linear-gradient(145deg, #fee2e2, #fecaca);
            box-shadow: 
              12px 12px 24px rgba(239, 68, 68, 0.2),
              -12px -12px 24px rgba(255, 255, 255, 0.8),
              inset 2px 2px 4px rgba(239, 68, 68, 0.1);
          }

          .mute-button:hover {
            transform: translateY(-2px);
          }

          .chat-button {
            padding: 12px 20px;
            background: linear-gradient(135deg, rgba(30, 118, 242, 0.93) 0%, rgba(134, 191, 255, 0.78) 100%);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(238, 238, 238, 1);
            border-radius: 50px;
            color: #ffffff;
            font-size: 13px;
            font-weight: 500;
            box-shadow: inset 0 1.7px 1.5px rgba(255, 255, 255, 1), 0 4px 12px rgba(0, 0, 0, 0.1);
            cursor: pointer;
            transition: all 0.35s ease;
          }

          .chat-button:hover {
            transform: translateY(-2px);
          }

          .close-button {
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: linear-gradient(145deg, #ffffff, #f5f5f5);
            box-shadow: 
              12px 12px 24px rgba(0, 0, 0, 0.15),
              -12px -12px 24px rgba(255, 255, 255, 0.8),
              inset 2px 2px 4px rgba(0, 0, 0, 0.05);
          }

          .close-button:hover {
            transform: translateY(-2px);
          }

          .btn-inner {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
          }

          @media (max-width: 480px) {
            .orb-modal-container {
              max-width: 100%;
              padding: 16px 16px 32px;
            }

            .orb-modal-title {
              font-size: 15px;
            }

            .orb-controls-section {
              gap: 8px;
            }

            .dot.alex {
              width: 18px;
            }
          }
            /* ===== TABLET (768px - 1024px) - FULL WIDTH ===== */
@media (min-width: 768px) and (max-width: 1023px) {
  .orb-modal-overlay {
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

  .orb-modal-container {
    max-width: 100%;
    width: 100%;
    max-height: 85vh;
    border-radius: 32px 32px 0 0;
    padding: 28px 24px 50px;
  }

  .orb-modal-title {
    font-size: 18px;
    margin-bottom: 20px;
  }

  .orb-center-section {
    min-height: 140px;
    margin: 30px 0;
  }

  .dot.alex {
    width: 24px;
  }

  .dots-alex-container {
    gap: 10px;
    height: 90px;
  }

  .orb-status-text {
    font-size: 14px;
    margin-bottom: 28px;
  }

  .orb-controls-section {
    padding-top: 20px;
    gap: 12px;
  }

  .mute-button,
  .close-button {
    width: 60px;
    height: 60px;
  }

  .chat-button {
    padding: 14px 24px;
    font-size: 14px;
  }
}

/* ===== DESKTOP (1024px+) - FULL WIDTH ===== */
@media (min-width: 1024px) {
  .orb-modal-overlay {
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  .orb-modal-container {
    max-width: 100%;
    width: 100%;
    max-height: 85vh;
    border-radius: 36px 36px 0 0;
    padding: 32px 32px 60px;
    box-shadow: 0 -12px 48px rgba(0, 0, 0, 0.25);
  }

  .orb-modal-title {
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 24px;
  }

  .orb-center-section {
    min-height: 160px;
    margin: 40px 0;
  }

  .dot.alex {
    width: 26px;
  }

  .dots-alex-container {
    gap: 12px;
    height: 100px;
  }

  .orb-status-text {
    font-size: 15px;
    margin-bottom: 32px;
  }

  .orb-controls-section {
    padding-top: 24px;
    gap: 16px;
  }

  .mute-button,
  .close-button {
    width: 64px;
    height: 64px;
  }

  .mute-button .btn-inner svg,
  .close-button .btn-inner svg {
    width: 24px;
    height: 24px;
  }

  .chat-button {
    padding: 15px 28px;
    font-size: 15px;
    font-weight: 500;
  }

  .thinking-indicator {
    gap: 16px;
  }

  .dot.thinking {
    width: 14px;
    height: 14px;
  }

  .muted-icon-center svg,
  .muted-indicator svg {
    width: 32px;
    height: 32px;
  }
}
        `}</style>
      </div>
    </div>
  );
};

export default VoiceOrbModal;